import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Extracts an Instagram post ID from a public URL.
 * Supports https://www.instagram.com/p/{ID}/ , /reel/{ID}/ , /tv/{ID}/
 */
const extractPostId = (url) => {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(?:p|reel|tv)\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
};

export const InstagramSection = () => {
  const [section, setSection] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/instagram`)
      .then((res) => setSection(res.data))
      .catch(() => setSection(null));
  }, []);

  if (!section || !section.enabled || !section.posts?.length) return null;

  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="relative py-24 lg:py-32 bg-[#FBF6EA] overflow-hidden"
    >
      <div className="absolute top-[20%] right-[-5%] w-[35vmin] h-[35vmin] rounded-full bg-[#A0522D]/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[15%] left-[-5%] w-[28vmin] h-[28vmin] rounded-full bg-[#7AB8D6]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-14"
        >
          <p className="overline text-[#4F8FA8] mb-5">{section.eyebrow}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2A1810]">
            {section.heading_line1}
            <br />
            <span className="font-serif-italic text-[#A0522D]">
              {section.heading_italic}
            </span>
          </h2>
          {section.description && (
            <p className="mt-5 text-[#6E5B4A] text-base sm:text-lg max-w-xl leading-relaxed">
              {section.description}
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {section.posts.map((post, i) => {
            const id = extractPostId(post.url);
            if (!id) return null;
            return (
              <motion.div
                key={`${id}-${i}`}
                data-testid={`instagram-post-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="rounded-sm overflow-hidden shadow-[0_15px_30px_-15px_rgba(160,82,45,0.25)] bg-white border border-[#D9C5A4]"
              >
                <iframe
                  src={`https://www.instagram.com/p/${id}/embed/captioned`}
                  title={post.caption || `Post Instagram ${i + 1}`}
                  className="w-full bg-white"
                  style={{ height: "560px", border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  scrolling="no"
                />
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a
            href="https://www.instagram.com/mantelheelsexperience/"
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-cta-profile"
            className="btn-primary inline-flex"
          >
            <Instagram size={16} /> Apri il mio profilo Instagram
          </a>
        </div>
      </div>
    </section>
  );
};
